(() => {
  "use strict";

  const SELECTOR = "[data-image-selector]";
  const DEFAULT_EXPORT_LABEL = "Selected Image";
  const PLACEHOLDER_COLOURS = [
    "263238", "455a64", "37474f", "546e7a", "607d8b",
    "78909c", "29434e", "3e515b", "4f626c", "60727b",
    "324a5f", "405d72", "526f82", "647f90", "795548",
    "6d4c41", "5d4037", "4e342e", "3e2723", "8d6e63"
  ];

  const normalize = (value) =>
    String(value || "").replace(/\s+/g, " ").trim().toLowerCase();

  const compact = (value) => normalize(value).replace(/[^a-z0-9]+/g, "");

  const createPlaceholder = (label, colour) => {
    const safeLabel = String(label || "Image").replace(/[<>&"']/g, "");
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="1600" height="1067" viewBox="0 0 1600 1067">
        <rect width="1600" height="1067" fill="#${colour}"/>
        <path d="M0 850 360 500l250 242 240-188 390 296Z" fill="#fff" opacity=".12"/>
        <circle cx="1250" cy="270" r="105" fill="#fff" opacity=".12"/>
        <text x="800" y="535" fill="#fff" font-family="Arial,sans-serif"
          font-size="92" font-weight="700" text-anchor="middle" dominant-baseline="middle">${safeLabel}</text>
        <text x="800" y="630" fill="#fff" opacity=".72" font-family="Arial,sans-serif"
          font-size="34" text-anchor="middle">Landscape image placeholder</text>
      </svg>
    `;

    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
  };

  const ensureLightbox = () => {
    let lightbox = document.querySelector("[data-image-lightbox]");
    if (lightbox) {
      /*
        A transformed form/app container changes how position: fixed behaves.
        Moving the modal under body keeps it relative to the viewport.
      */
      if (lightbox.parentElement !== document.body) {
        document.body.appendChild(lightbox);
      }
      return lightbox;
    }

    lightbox = document.createElement("div");
    lightbox.className = "orms-image-lightbox";
    lightbox.dataset.imageLightbox = "";
    lightbox.setAttribute("role", "dialog");
    lightbox.setAttribute("aria-modal", "true");
    lightbox.setAttribute("aria-label", "Large image preview");
    lightbox.hidden = true;
    lightbox.innerHTML = `
      <button class="orms-image-lightbox__backdrop" type="button"
        data-close-preview aria-label="Close image preview"></button>
      <figure class="orms-image-lightbox__content">
        <button class="orms-image-lightbox__close" type="button"
          data-close-preview aria-label="Close image preview">×</button>
        <img class="orms-image-lightbox__image" data-lightbox-image src="" alt="">
        <figcaption class="orms-image-lightbox__caption" data-lightbox-caption></figcaption>
      </figure>
    `;
    document.body.appendChild(lightbox);
    return lightbox;
  };

  const findExportField = (selector) => {
    const requestedLabel =
      selector.dataset.exportFieldLabel || DEFAULT_EXPORT_LABEL;
    const form =
      selector.closest(".globo-formbuilder") ||
      selector.closest("form") ||
      document.querySelector(".globo-formbuilder");

    if (!form) return null;

    const writableSelector = [
      'input:not([type="radio"]):not([type="checkbox"]):not([type="submit"]):not([type="button"]):not([type="file"])',
      "textarea",
      "select"
    ].join(",");
    const requestedKey = compact(requestedLabel);
    const matchesRequestedLabel = (value) =>
      compact(value).includes(requestedKey);
    const getContainer = (element) =>
      element?.closest(
        ".globo-form-control, .form-group, .field, [data-field-id], [data-id]"
      ) || element?.parentElement;
    const makeResult = (input, container) =>
      input && !selector.contains(input)
        ? { input, container: container || getContainer(input) }
        : null;

    /* Optional escape hatch when a store uses unusual generated markup. */
    if (selector.dataset.exportFieldSelector) {
      try {
        const explicitInput =
          form.querySelector(selector.dataset.exportFieldSelector) ||
          document.querySelector(selector.dataset.exportFieldSelector);
        const explicitResult = makeResult(explicitInput);
        if (explicitResult) return explicitResult;
      } catch (error) {
        console.warn("Invalid Selected Image field selector", error);
      }
    }

    const getStoredLabel = (element) =>
      [
        element?.textContent,
        element?.getAttribute?.("data-label"),
        element?.querySelector?.("[data-label]")?.getAttribute("data-label")
      ].join(" ");

    const labels = Array.from(form.querySelectorAll("label")).filter(
      (label) =>
        !selector.contains(label) && matchesRequestedLabel(getStoredLabel(label))
    );

    for (const label of labels) {
      let input = null;
      const targetId = label.getAttribute("for");
      if (targetId) {
        const escapedId =
          typeof CSS !== "undefined" && CSS.escape
            ? CSS.escape(targetId)
            : targetId.replace(/([ #;?%&,.+*~':"!^$[\]()=>|/@])/g, "\\$1");
        input =
          form.querySelector(`#${escapedId}`) || document.getElementById(targetId);
      }

      const container = getContainer(label);
      input = input || container?.querySelector(writableSelector);
      const result = makeResult(input, container);
      if (result) return result;
    }

    /*
      Some Powerful Form Builder themes render the field title as a div/span
      rather than a label. Score every writable field using its attributes and
      surrounding field text so required asterisks and help text do not matter.
    */
    const candidates = Array.from(form.querySelectorAll(writableSelector))
      .filter((input) => !selector.contains(input))
      .map((input) => {
        const container = getContainer(input);
        const description = [
          input.id,
          input.getAttribute("name"),
          input.getAttribute("aria-label"),
          input.getAttribute("placeholder"),
          input.dataset?.label,
          container?.getAttribute?.("data-label"),
          container?.querySelector?.("[data-label]")?.getAttribute("data-label"),
          container?.textContent
        ].join(" ");
        let score = matchesRequestedLabel(description) ? 10 : 0;
        if (input.type === "hidden") score -= 2;
        if (matchesRequestedLabel(input.getAttribute("aria-label"))) score += 5;
        if (matchesRequestedLabel(input.getAttribute("name"))) score += 4;
        if (matchesRequestedLabel(input.id)) score += 3;
        return { input, container, score };
      })
      .filter((candidate) => candidate.score > 0)
      .sort((a, b) => b.score - a.score);

    return candidates[0] || null;
  };

  const setExportValue = (field, value) => {
    const previousValue = field.input.value;
    const prototype =
      field.input instanceof HTMLTextAreaElement
        ? HTMLTextAreaElement.prototype
        : field.input instanceof HTMLSelectElement
          ? HTMLSelectElement.prototype
          : HTMLInputElement.prototype;
    const setter = Object.getOwnPropertyDescriptor(prototype, "value")?.set;

    field.input.focus({ preventScroll: true });
    try {
      field.input.dispatchEvent(
        new InputEvent("beforeinput", {
          bubbles: true,
          composed: true,
          inputType: "insertText",
          data: value
        })
      );
    } catch (_error) {
      field.input.dispatchEvent(new Event("beforeinput", { bubbles: true }));
    }

    /* React-style controlled inputs compare against their private tracker. */
    if (field.input._valueTracker) {
      field.input._valueTracker.setValue(previousValue);
    }

    if (setter) setter.call(field.input, value);
    else field.input.value = value;

    /* Keep both the live property and serialized HTML attribute in sync. */
    field.input.setAttribute("value", value);

    try {
      field.input.dispatchEvent(
        new InputEvent("input", {
          bubbles: true,
          composed: true,
          inputType: "insertText",
          data: value
        })
      );
    } catch (_error) {
      field.input.dispatchEvent(new Event("input", { bubbles: true }));
    }
    field.input.dispatchEvent(new Event("change", { bubbles: true }));
    field.input.dispatchEvent(new KeyboardEvent("keyup", { bubbles: true }));

    /* Powerful Form Builder versions that use jQuery receive their own events. */
    if (window.jQuery) {
      window.jQuery(field.input).val(value).trigger("input").trigger("change");
    }

    field.input.blur();
  };

  const initSelector = (selector) => {
    if (selector.dataset.themeReady === "true") return;
    selector.dataset.themeReady = "true";

    const lightbox = ensureLightbox();
    const lightboxImage = lightbox.querySelector("[data-lightbox-image]");
    const lightboxCaption = lightbox.querySelector("[data-lightbox-caption]");
    const closeButton = lightbox.querySelector(".orms-image-lightbox__close");
    const visualRadios = Array.from(
      selector.querySelectorAll('.orms-image-option__choice input[type="radio"]')
    );
    let exportField = null;
    let lastTrigger = null;

    const connectExportField = () => {
      exportField = findExportField(selector);
      if (!exportField) return false;

      exportField.container?.classList.add("orms-native-export-field");
      selector.dataset.exportConnected = "true";

      /*
        These radios are visual controls only. Removing their names prevents
        duplicate/unknown fields from being submitted by the app.
      */
      visualRadios.forEach((radio) => {
        radio.removeAttribute("name");
        radio.removeAttribute("required");
      });
      return true;
    };

    selector.querySelectorAll("[data-preview-image]").forEach((button, index) => {
      const thumbnail = button.querySelector("img");
      const optionName =
        button
          .closest(".orms-image-option")
          ?.querySelector(".orms-image-option__name")
          ?.textContent.trim() || `Entry ${String(index + 1).padStart(2, "0")}`;
      const fallback = createPlaceholder(
        optionName,
        PLACEHOLDER_COLOURS[index % PLACEHOLDER_COLOURS.length]
      );

      if (!thumbnail) return;

      const source = thumbnail.getAttribute("src") || "";
      if (!source || source.includes("placehold.co")) {
        thumbnail.src = fallback;
        button.dataset.fullImage = fallback;
      } else {
        thumbnail.addEventListener(
          "error",
          () => {
            thumbnail.src = fallback;
            button.dataset.fullImage = fallback;
          },
          { once: true }
        );
      }
    });

    const closePreview = () => {
      if (lightbox.hidden) return;
      lightbox.hidden = true;
      lightboxImage.removeAttribute("src");
      document.body.classList.remove("orms-lightbox-open");
      lastTrigger?.focus();
    };

    const openPreview = (button) => {
      const thumbnail = button.querySelector("img");
      const option = button.closest(".orms-image-option");
      const name =
        option?.querySelector(".orms-image-option__name")?.textContent.trim() ||
        "";

      if (!thumbnail) return;
      lastTrigger = button;
      lightboxImage.src =
        button.dataset.fullImage ||
        thumbnail.currentSrc ||
        thumbnail.getAttribute("src") ||
        thumbnail.src;
      lightboxImage.alt = thumbnail.alt || name;
      lightboxCaption.textContent = name;
      lightbox.hidden = false;
      document.body.classList.add("orms-lightbox-open");
      closeButton.focus();
    };

    selector.addEventListener("click", (event) => {
      const previewButton = event.target.closest("[data-preview-image]");
      if (previewButton) openPreview(previewButton);
    });

    visualRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        visualRadios.forEach((other) => {
          const selected = other === radio;
          other.checked = selected;
          other
            .closest(".orms-image-option")
            ?.classList.toggle("is-selected", selected);
        });

        if (!exportField) connectExportField();
        if (exportField) {
          setExportValue(exportField, radio.value);
          selector.dataset.exportValue = radio.value;
        } else {
          selector.dataset.exportConnected = "false";
          console.warn(
            `Image selector could not find the Powerful Form Builder field "${
              selector.dataset.exportFieldLabel || DEFAULT_EXPORT_LABEL
            }".`
          );
        }
      });
    });

    const form =
      selector.closest("form") ||
      selector.closest(".globo-formbuilder")?.querySelector("form");
    const syncSelectedValue = () => {
      const selected = visualRadios.find((radio) => radio.checked);
      if (!selected) return;
      if (!exportField) connectExportField();
      if (exportField) setExportValue(exportField, selected.value);
    };

    if (form && !form.dataset.imageExportSyncReady) {
      form.dataset.imageExportSyncReady = "true";
      form.addEventListener("submit", syncSelectedValue, true);
      form.addEventListener(
        "click",
        (event) => {
          if (event.target.closest('button[type="submit"], input[type="submit"]')) {
            syncSelectedValue();
          }
        },
        true
      );
    }

    if (!lightbox.dataset.eventsReady) {
      lightbox.dataset.eventsReady = "true";
      lightbox.addEventListener("click", (event) => {
        if (event.target.closest("[data-close-preview]")) closePreview();
      });
      document.addEventListener("keydown", (event) => {
        if (event.key === "Escape") closePreview();
      });
    }

    connectExportField();
    [250, 1000, 2500].forEach((delay) => {
      window.setTimeout(() => {
        if (!exportField && connectExportField()) {
          const selected = visualRadios.find((radio) => radio.checked);
          if (selected) setExportValue(exportField, selected.value);
        }
      }, delay);
    });
  };

  const initAll = () => {
    document.querySelectorAll(SELECTOR).forEach(initSelector);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll, { once: true });
  } else {
    initAll();
  }

  /*
    Powerful Form Builder renders asynchronously, so watch for a gallery added
    after the theme JavaScript has already loaded.
  */
  new MutationObserver(initAll).observe(document.documentElement, {
    childList: true,
    subtree: true
  });
})();
